import Observer from '@researchgate/react-intersection-observer';
import classNames from 'classnames';
import {observer} from 'mobx-react';
import React, {useMemo, useState} from 'react';

import {PAGE_TYPE_SUBS_LANDING_PAGE, UI_REGION} from '@udemy/browse-utils';
import {DiscoveryUnit, DiscoveryUnitItem} from '@udemy/discovery-api';
import {useI18n} from '@udemy/i18n';
import {DiscoveryUnitsContainer} from '@udemy/react-discovery-units';
import {
    CMSConnectedPartnersBanner,
    DynamicWebContentPageType,
} from '@udemy/shared-one-udemy-components';
import {
    ConsumerSubscriptionPlan,
    DateIntervalType,
    useConsumerSubscriptionPlan,
} from '@udemy/subscription-graphql';

import {WindowShoppingUnit} from 'src/components/window-shopping-unit/window-shopping-unit.react-component';

import {CollectionTitle} from './components/collection-title/collection-title.react-component';
import {ConsumerSubscriptionStickyHeader} from './components/consumer-subscription-sticky-header/consumer-subscription-sticky-header.react-component';
import {PersonalPlanBannerWrapper} from './components/personal-plan-banner/personal-plan-banner-wrapper.react-component';
import {PersonalPlanChecklist} from './components/personal-plan-checklist/personal-plan-checklist.react-component';
import {PersonalPlanFAQSkeleton} from './components/personal-plan-faq/personal-plan-faq-skeleton.react-component';
import {PersonalPlanFAQ} from './components/personal-plan-faq/personal-plan-faq.react-component';
import {PersonalPlanHeroWrapper} from './components/personal-plan-hero/personal-plan-hero-wrapper.react-component';
import {PersonalPlanSubscriptionCTA} from './components/personal-plan-subscription-cta/personal-plan-subscription-cta.react-component';
import {PersonalPlanTestimonials} from './components/personal-plan-testimonials/personal-plan-testimonials.react-component';
import {PersonalPlanCards} from './components/plan-cards/personal-plan-cards.react-component';
import {PersonalPlanComparisonSkeleton} from './components/plan-cards/personal-plan-comparison-skeleton.react-component';
import {PlanCatalogPreview} from './components/plan-catalog-preview/plan-catalog-preview.react-component';
import {TrustedNumbers} from './components/trusted-numbers/trusted-numbers.react-component';
import {ValuePropSkeleton} from './components/value-prop/value-prop-skeleton.react-component';
import {ValueProp} from './components/value-prop/value-prop.react-component';
import {useCoursesPriceRange} from './hooks/useCoursesPriceRange';
import styles from './personal-plan-landing-page-body.module.less';
import {
    useNonCatalogCheckoutSource,
    usePersonalPlanSubscriptionCheckoutEntry,
} from './personal-plan-subscription-checkout-entry-context';
import {getValuePropsConfig} from './utils/personal-plan-value-props-config';

export interface PersonalPlanLandingPageProps {
    isPPLPRevamp: boolean;
    isPPLPCompleteRevamp?: boolean;
}

interface PersonalPlanLandingPageBodyProps extends PersonalPlanLandingPageProps {
    /** True while feature-flag assignments for this page are still resolving. */
    isExperimentationLoading?: boolean;
}

export const PersonalPlanLandingPageBody = observer(
    ({
        isPPLPRevamp,
        isPPLPCompleteRevamp = false,
        isExperimentationLoading = false,
    }: PersonalPlanLandingPageBodyProps) => {
        const i18nApi = useI18n();
        const {gettext} = i18nApi;
        const [isFloatingHeaderVisible, setFloatingHeaderVisibility] = React.useState(false);
        const handleIntersectionChange = React.useCallback((ev) => {
            setFloatingHeaderVisibility(!ev.isIntersecting);
        }, []);

        const valuePropsConfig = useMemo(
            () => getValuePropsConfig(gettext, isPPLPRevamp, isPPLPCompleteRevamp),
            [gettext, isPPLPRevamp, isPPLPCompleteRevamp],
        );

        // Subscription plan loading and derived state
        const [personalPlanIsLoading, setPersonalPlanIsLoading] = useState(true);
        const personalPlanContext = useConsumerSubscriptionPlan();
        const {consumerSubscriptionPlan, isPersonalPlanSubscriber, renderSubscriptionCTA} =
            useMemo(() => {
                setPersonalPlanIsLoading(personalPlanContext?.isLoading ?? true);
                const isPersonalPlanSubscriber =
                    (personalPlanContext &&
                        'isSubscriberToSpecificPlan' in personalPlanContext &&
                        personalPlanContext.isSubscriberToSpecificPlan) ??
                    false;
                const renderSubscriptionCTA = personalPlanContext && !personalPlanIsLoading;
                const consumerSubscriptionPlan =
                    !personalPlanIsLoading &&
                    !isPersonalPlanSubscriber &&
                    personalPlanContext &&
                    'id' in personalPlanContext
                        ? (personalPlanContext as ConsumerSubscriptionPlan)
                        : undefined;
                return {consumerSubscriptionPlan, isPersonalPlanSubscriber, renderSubscriptionCTA};
            }, [personalPlanContext, personalPlanIsLoading]);

        const {isCoursesPriceRangeLoading, coursesPriceRange} = useCoursesPriceRange();
        const subscriptionCheckoutEntry = usePersonalPlanSubscriptionCheckoutEntry();
        const {source: nonCatalogSource, noticeDataLoading: nonCatalogNoticeDataLoading} =
            useNonCatalogCheckoutSource();

        const collectionCta = renderSubscriptionCTA ? (
            <PersonalPlanSubscriptionCTA
                plan={personalPlanContext}
                planPreference={DateIntervalType.Month}
                className={styles['window-shopping-cta']}
                uiRegion={UI_REGION.SUBS_COLLECTION_SAMPLER}
                showSubtitle={false}
                subscriptionCheckoutEntry={subscriptionCheckoutEntry}
                source={nonCatalogSource}
                noticeDataLoading={nonCatalogNoticeDataLoading}
            />
        ) : null;

        const getUnitComponentAndProps = (unit: Partial<DiscoveryUnit<DiscoveryUnitItem>>) => {
            if (!unit) throw new Error('Missing unit');

            switch (unit.type) {
                case 'bestseller':
                    return {
                        component: WindowShoppingUnit,
                        componentProps: {
                            renderCta: collectionCta,
                        },
                    };
                default:
                    throw new Error(`Missing component for unit type: ${unit.type}`);
            }
        };

        // Hold the comparison skeleton only while the experiment is unresolved so
        // treatment does not briefly paint the control checklist. Do not wait on
        // the plan query: unsupported countries resolve as no plan (`undefined`)
        // and still need the cards/checklist with a disabled CTA.
        const renderPlanSection = () => {
            if (isExperimentationLoading) {
                return <PersonalPlanComparisonSkeleton />;
            }

            if (isPPLPCompleteRevamp) {
                return (
                    <PersonalPlanCards
                        plan={consumerSubscriptionPlan}
                        isCoursesPriceRangeLoading={isCoursesPriceRangeLoading}
                        coursesPriceRange={coursesPriceRange}
                    />
                );
            }

            return (
                <PersonalPlanChecklist
                    plan={consumerSubscriptionPlan}
                    isSubscriber={isPersonalPlanSubscriber}
                    isCoursesPriceRangeLoading={isCoursesPriceRangeLoading}
                    coursesPriceRange={coursesPriceRange}
                    isPPLPRevamp={isPPLPRevamp}
                />
            );
        };

        const renderHeroAndStats = () => {
            if (isPPLPCompleteRevamp) {
                return (
                    <div>
                        <Observer onChange={handleIntersectionChange}>
                            <div>
                                <PersonalPlanHeroWrapper />
                            </div>
                        </Observer>
                        <PersonalPlanTestimonials />
                    </div>
                );
            }

            return (
                <Observer onChange={handleIntersectionChange}>
                    <div className={classNames('ud-container', styles['banner-and-stats-wrapper'])}>
                        <PersonalPlanBannerWrapper isPPLPRevamp={isPPLPRevamp} />
                        <TrustedNumbers
                            className={styles['updated-trusted-numbers']}
                            isPPLPRevamp={isPPLPRevamp}
                        />
                    </div>
                </Observer>
            );
        };

        return (
            <>
                {!personalPlanIsLoading && personalPlanContext ? (
                    <ConsumerSubscriptionStickyHeader
                        label={gettext('Personal Plan')}
                        subscriptionContext={personalPlanContext}
                        visible={isFloatingHeaderVisible}
                    />
                ) : undefined}
                <div className={styles.wrapper}>
                    {renderHeroAndStats()}
                    <div
                        className={classNames(styles['updated-partners-banner-wrapper'], {
                            'component-margin': !isPPLPCompleteRevamp,
                            [styles['partners-below-testimonials']]: isPPLPCompleteRevamp,
                        })}
                    >
                        <div className="ud-container">
                            <div className={styles['partners']}>
                                <CMSConnectedPartnersBanner
                                    pageType={DynamicWebContentPageType.PersonalPlanLandingPage}
                                />
                            </div>
                        </div>
                    </div>
                    <div
                        className={classNames('ud-container', styles['landing-page'], {
                            [styles['landing-page-revamp-container']]: isPPLPCompleteRevamp,
                        })}
                    >
                        <section
                            className={classNames(styles['value-props'], {
                                [styles['value-props-stacked']]: isPPLPCompleteRevamp,
                            })}
                        >
                            {isExperimentationLoading ? (
                                <ValuePropSkeleton />
                            ) : (
                                valuePropsConfig.map((valueProp) => (
                                    <ValueProp
                                        key={valueProp.id}
                                        imgSrc={valueProp.imgSrc}
                                        slogan={valueProp.slogan}
                                        listItems={valueProp.listItems}
                                        // The complete revamp spaces its rows with a `gap` on the
                                        // section instead of the page-wide `component-margin` rhythm.
                                        className={
                                            isPPLPCompleteRevamp ? undefined : 'component-margin'
                                        }
                                        heading={valueProp.heading}
                                        content={valueProp.content}
                                        isPPLPRevamp={isPPLPRevamp}
                                        isPPLPCompleteRevamp={isPPLPCompleteRevamp}
                                    />
                                ))
                            )}
                            {!isPPLPCompleteRevamp && !isExperimentationLoading && (
                                <div className="component-margin">
                                    <CollectionTitle
                                        className={styles['collection-title']}
                                        isPPLPRevamp={isPPLPRevamp}
                                    />
                                    <DiscoveryUnitsContainer
                                        pageType={PAGE_TYPE_SUBS_LANDING_PAGE}
                                        itemCount={6}
                                        getUnitComponentAndProps={getUnitComponentAndProps}
                                    />
                                </div>
                            )}
                        </section>
                        {isPPLPCompleteRevamp && (
                            <div className="component-margin">
                                <PlanCatalogPreview />
                            </div>
                        )}
                    </div>
                    {renderPlanSection()}
                    <div className={styles['faq']}>
                        {isExperimentationLoading ? (
                            <PersonalPlanFAQSkeleton />
                        ) : (
                            <PersonalPlanFAQ isPPLPCompleteRevamp={isPPLPCompleteRevamp} />
                        )}
                    </div>
                </div>
            </>
        );
    },
);
